import { Control, DataField, Question } from "interfaces/Phoenix.FormsDesigner.Models";

export interface DesignerQuestionDefinition {
  icon: string,
  name: string,
  description: string,
  control?: Partial<Control>,
  controlIsContainer?: boolean, // true for groups and repeats beacuse they contain other controls
};

export const designerQuestionDefs: DesignerQuestionDefinition[] = [
  {
    icon: "list",
    name: "Dropdown",
    description: "A dropdown list of choices.",
    control: {
      type: "SelectOne",
      appearance: "compact",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "list",
    name: "Searchable List",
    description: "A searchable list of choices.",
    control: {
      type: "SelectOne",
      appearance: "minimal",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "list",
    name: "Combo Box",
    description: "Choose from a list of suggestions or enter free-form text.",
    control: {
      type: "SelectOne",
      appearance: "phoenix:combo",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "check_box",
    name: "Radios",
    description: "A list of radio buttons.",
    control: {
      type: "SelectOne",
      appearance: "full",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "check_box",
    name: "Checkboxes",
    description: "A checkbox list.",
    control: {
      type: "SelectMany",
      appearance: "full",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "check_box",
    name: "Checkbox Group",
    description: "A group of single checkbox controls.",
    control: {
      type: "SelectGroup",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "numbers",
    name: "Integer",
    description: "An integer input.",
    control: {
      type: "TextBox",
      dataField: {
        type: "Integer",
      } as DataField,
    },
  },
  {
    icon: "numbers",
    name: "Decimal",
    description: "A decimal input.",
    control: {
      type: "TextBox",
      dataField: {
        type: "Decimal",
      } as DataField,
    },
  },
  {
    icon: "short_text",
    name: "Textbox",
    description: "A single line text field.",
    control: {
      type: "TextBox",
      dataField: {
        type: "String",
      } as DataField,
    },
  },
  {
    icon: "subject",
    name: "Text Area",
    description: "A large, multi-line textbox suitable for narrative comments.",
    control: {
      type: "TextArea",
      dataField: {
        type: "String",
      } as DataField,
    },
  },
  {
    icon: "date_range",
    name: "Date",
    description: "A date input field.",
    control: {
      type: "TextBox",
      dataField: {
        type: "Date",
      } as DataField,
    },
  },
  {
    icon: "history_toggle_off",
    name: "Time",
    description: "A time input field.",
    control: {
      type: "TextBox",
      dataField: {
        type: "Time",
      } as DataField,
    },
  },
  {
    icon: "short_text",
    name: "Label",
    description: "A label.",
    control: {
      type: "Label",
      dataField: {
        type: "String",
      } as DataField,
    },
  },
  {
    icon: "splitscreen",
    name: "Group",
    description: "Collapsable container for other controls.",
    controlIsContainer: true,
    control: {
      type: "Group",
      controls: [],
    },
  },
  {
    icon: "repeat",
    name: "Repeat",
    description: "A repeatable collection of controls.",
    controlIsContainer: true,
    control: {
      type: "Repeat",
      controls: [],
    },
  },
  {
    icon: "repeat",
    name: "Collapsible Repeat",
    description: "A collapsible repeatable collection of controls.",
    controlIsContainer: true,
    control: {
      type: "Repeat",
      appearance: "compact",
      controls: [],
    },
  },
  {
    icon: "photo_camera",
    name: "Photo",
    description: "",
    control: {
      type: "Photo",
    },
  },
  {
    icon: "list",
    name: "Dynamic Dropdown",
    description: "A dropdown with choices that can change based on a condition.",
    control: {
      type: "DynamicItemsetSelectOne",
      appearance: "compact",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "list",
    name: "Advanced Dynamic Dropdown",
    description: "A dropdown with choices that are defined strictly via xpath.",
    control: {
      type: "AdvancedDynamicItemsetSelectOne",
      appearance: "compact",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "list",
    name: "Advanced Dynamic Searchable List",
    description: "A searchable list of choices that are defined strictly via xpath.",
    control: {
      type: "AdvancedDynamicItemsetSelectOne",
      appearance: "minimal",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "list",
    name: "Advanced Dynamic Combo Box",
    description: "Choose from a list of suggestions that are defined strictly via xpath. or enter free-form text.",
    control: {
      type: "AdvancedDynamicItemsetSelectOne",
      appearance: "phoenix:combo",
      dataField: {
        type: "Token",
      } as DataField,
    },
  },
  {
    icon: "calculate",
    name: "Calculate",
    description: "An embedded calculate expression.",
    control: {
      type: "Calculate",
    },
  },
  {
    icon: "square_foot",
    name: "Ruleset",
    description: "An embedded ruleset.",
    control: {
      type: "Ruleset",
      appearance: "compact",
    },
  },
  {
    icon: "repeat",
    name: "Readonly Repeat",
    description: "A collapsible collection of controls without the add or delete button.",
    controlIsContainer: true,
    control: {
      type: "Repeat",
      appearance: "static",
      controls: [],
    },
  },
  {
    icon: "square_foot",
    name: "Recommendation",
    description: "An embedded recommendation.",
    control: {
      type: "Recommendation",
      appearance: "compact",
      controls: [],
    },
  },
  {
    icon: "attach_file",
    name: "File",
    description: "A file upload control.",
    control: {
      type: "File",
    },
  },
];

// ONLY == NOT === here because of null !== undefined issues and stuff
export const findQuestionDef = (question: Question) =>
    designerQuestionDefs.find(d =>
      d.control.type == question.control.type &&
      d.control.appearance == question.control.appearance &&
      d.control.dataField?.type == question.control.dataField?.type);
